import { type MonetaryValue, add, toNumber } from 'ezmoney';

export type BudgetBreakdown = {
  used: MonetaryValue;
  usedExceeded: MonetaryValue;
  committed: MonetaryValue;
  committedExceeded: MonetaryValue;
  available: MonetaryValue;
};

export type Breakdown = BudgetBreakdown & {
  breakdownComputedAt: Date;
};

export type BreakdownPart = keyof Pick<BudgetBreakdown, 'used' | 'committed'>;

/**
 * Calculates the sum of used and committed amounts.
 * @param breakdown The budget breakdown.
 * @returns the sum of committed + used amounts.
 */
const getSpentInBudgetTotalAmount = ({
  committed,
  used,
}: BudgetBreakdown): MonetaryValue => add(committed, used);

/**
 * Calculates the budget limit.
 * @param breakdown The budget breakdown.
 * @returns the sum of `used` + `committed` + `available` amounts.
 */
export const getBudgetLimit = (breakdown: BudgetBreakdown): MonetaryValue =>
  add(getSpentInBudgetTotalAmount(breakdown), breakdown.available);

/**
 * Calculates the used amount.
 * @param breakdown The budget breakdown.
 * @param withExceeded Includes `usedExceeded` ?
 * @returns the sum of `used` + `usedExceeded` if `withExceeded` is truthy, `used` only otherwise
 */
export const getUsedAmount = (
  { used, usedExceeded }: BudgetBreakdown,
  withExceeded = true,
): MonetaryValue => (withExceeded ? add(used, usedExceeded) : used);

/**
 * Calculates the committed amount.
 * @param breakdown The budget breakdown.
 * @param withExceeded Includes `committedExceeded` ?
 * @returns the sum of `committed` + `committedExceeded` if `withExceeded` is truthy, `committed` only otherwise
 */
export const getCommittedAmount = (
  { committed, committedExceeded }: BudgetBreakdown,
  withExceeded = true,
): MonetaryValue =>
  withExceeded ? add(committed, committedExceeded) : committed;

export const hasCommittedAmounts = (breakdown: BudgetBreakdown) =>
  toNumber(getCommittedAmount(breakdown)) !== 0;

/**
 * Calculates the sum of exceeded amounts.
 * @param breakdown The budget breakdown.
 * @returns the sum of `committedExceeded` + `usedExceeded` amounts.
 */
export const getExceededAmount = ({
  committedExceeded,
  usedExceeded,
}: BudgetBreakdown): MonetaryValue => add(committedExceeded, usedExceeded);

/**
 * Calculates the available amount.
 * @param breakdown The budget breakdown.
 * @returns the sum of `committedExceeded` + `usedExceeded` amounts.
 */
export const getAvailableAmount = (breakdown: BudgetBreakdown): MonetaryValue =>
  breakdown.available;

/**
 * Checks if the budget breakdown has available amounts.
 * @param breakdown The budget breakdown.
 * @returns whether the budget breakdown has available amounts.
 */
export const hasAvailableAmounts = (breakdown: BudgetBreakdown) =>
  toNumber(breakdown.available) !== 0;

/**
 * Checks if the budget breakdown has exceeded amounts.
 * @param breakdown The budget breakdown.
 * @returns whether the budget breakdown has exceeded amounts.
 */
export const hasExceededAmounts = (breakdown: BudgetBreakdown) =>
  toNumber(getExceededAmount(breakdown)) !== 0;

/**
 * Calculates the sum of all spent amounts.
 * @param breakdown The budget breakdown.
 * @param withExceeded Includes `committedExceeded` and `usedExceeded` ?
 * @returns the sum of `used` + `usedExceeded` + `committed` + `committedExceeded` amounts.
 */
export const getSpentAmount = (
  breakdown: BudgetBreakdown,
  withExceeded = true,
): MonetaryValue =>
  add(
    getUsedAmount(breakdown, withExceeded),
    getCommittedAmount(breakdown, withExceeded),
  );

/**
 * Calculates the progress of the spent amounts
 * @param breakdown The budget breakdown.
 * @returns The ratio of spent amounts / budget limit
 */
export const getSpentBreakdownRatio = (breakdown: BudgetBreakdown): number =>
  toNumber(getSpentAmount(breakdown)) / toNumber(getBudgetLimit(breakdown));

/**
 * Calculates how much is left in the budget
 * @param breakdown The budget breakdown.
 * @returns The ratio of available amounts / budget limit
 */
export const getAvailableBreakdownRatio = (
  breakdown: BudgetBreakdown,
): number =>
  toNumber(breakdown.available) / toNumber(getBudgetLimit(breakdown));

/**
 * Calculates the exceeded amount ratio
 * @param breakdown The budget breakdown.
 * @returns The ratio of exceeded amounts / budget limit
 */
export const getExceededBreakdownRatio = (breakdown: BudgetBreakdown): number =>
  toNumber(getExceededAmount(breakdown)) / toNumber(getBudgetLimit(breakdown));

/**
 * Check if the budget breakdown is empty (all its amounts are equal to 0).
 * @param breakdown The budget breakdown.
 * @returns whether committed, used, committedExceeded, usedExceeded and available amounts are all empty.
 */
export const isBudgetBreakdownEmpty = ({
  committed,
  used,
  committedExceeded,
  usedExceeded,
  available,
}: BudgetBreakdown): boolean => {
  return [committed, used, committedExceeded, usedExceeded, available].every(
    (amount) => toNumber(amount) === 0,
  );
};
